import { TwoUIProgressView, TwoUIProgressViewOperationData } from "@amx/ux-twoui-telemetry";
import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { DefaultButton, IButtonStyles, IIconProps, ImageFit } from "@fluentui/react";
import React, { useRef } from "react";
import { IconName } from "../../../..";
import { ErrorCallout } from "../../../../components/ErrorCallout/ErrorCallout";
import { Text } from "../../../../components/Text/Text";
import { useComponentLogger } from "../../../../shared/logging";
import { InteractiveElementProps, validateProps } from "../../../../shared/props";
import { boxShadowDepths } from "../../../../styles/constants";
import { PillShimmer } from "./PillShimmer/PillShimmer";

export type Severity = "low" | "medium" | "high";

const getIconProps = (theme: Theme, pillMedia?: PillMediaProps, severity?: Severity): IIconProps => {
    if (pillMedia?.contentType === "image") {
        return {
            imageProps: {
                src: pillMedia.imgSrc,
                imageFit: ImageFit.contain,
                maximizeFrame: true
            },
            styles: {
                root: {
                    margin: "0px 8px 0px 0px",
                }
            }
        };
    } else {
        return {
            iconName: pillMedia?.iconName,
            styles: {
                root: {
                    color: getPillIconColor(theme, severity),
                    margin: "0px 8px 0px 0px",
                }
            }
        };
    }
};

const getTextVariant = (severity?: Severity) => {
    switch (severity) {
        case "high":
            return "errorBlockIcon";
        case "medium":
        case "low":
        default:
            return "bodyPrimaryText";
    }
};

const getPillIconColor = (theme: Theme, severity?: Severity) => {
    switch (severity) {
        case "high":
            return theme.semanticColors.errorBlockIcon;
        case "medium":
        case "low":
            return theme.palette.neutralPrimary;
        default:
            return theme.palette.neutralSecondary;
    }
};

const getPillBackground = (theme: Theme, severity?: Severity) => {
    switch (severity) {
        case "high":
            return theme.semanticColors.severeWarning;
        case "medium":
            return theme.semanticColors.warningBackground;
        case "low":
            return theme.palette.neutralLighterAlt;
        default:
            return theme.palette.neutralHigh;
    }
};

const getStyles = (theme: Theme, severity?: Severity): IButtonStyles => {
    const pillBgColor = getPillBackground(theme, severity);
    return {
        root: {
            background: pillBgColor,
            border: severity ? "0px" : `1px solid ${theme.palette.neutralHigh}`,
            borderRadius: 16,
            boxShadow: boxShadowDepths.rest,
            margin: "4px 4px 4px 0",
            padding: "6px 8px",
            selectors: {
                ":focus": {
                    background: theme.palette.neutralHigh,
                    border: `1px solid ${theme.palette.neutralHigh}`,
                },
                ":hover": {
                    background: theme.palette.neutralLighter,
                    border: `1px solid ${theme.palette.neutralSecondary}`
                },
                ":active": {
                    background: theme.palette.neutralLight,
                    border: `1px solid ${theme.palette.neutralLight}`
                },
                ":disabled": {
                    background: theme.palette.neutralLighter,
                    border: `1px solid ${theme.palette.neutralLighter}`,
                    boxShadow: "none"
                },
                ".ms-Fabric--isFocusVisible &:focus:after": {
                    borderRadius: 16,
                    borderColor: theme.palette.neutralSecondary,
                    outline: "none"
                }
            },
            maxWidth: "288px",
            minWidth: "34px"
        },
        rootHovered: {
            backgroundColor: pillBgColor,
            border: `1px solid ${theme.palette.neutralSecondary}`
        },
        rootFocused: {
            backgroundColor: pillBgColor
        },
        rootPressed: {
            backgroundColor: pillBgColor,
            border: `1px solid ${theme.palette.black}`,
        }
    };
};

/**
 * ImageProps interface for pill.
 */
export interface ImageProps {
    /**
     * Content type for pill media.
     */
    readonly contentType: "image";

    /**
     * Determines the source for the image
     */
    readonly imgSrc: string;
}

/**
 * IconProps interface for pill.
 */
export interface IconProps {
    /**
     * Content type for FinalColumn content on the left side.
     */
    readonly contentType: "icon";

    /**
     * Determines the icon type for pill
     */
    readonly iconName: IconName;
}

/**
 * Pill Media Props
 */
export type PillMediaProps = IconProps | ImageProps;

/**
 * Pill Props
 */
export interface PillProps extends InteractiveElementProps {
    /**
     * Information about the monitored operation.
     */
    readonly operationData?: PillOperationDataProps;

    /**
     * This value will be shown inside the Pill.
     */
    readonly text: string;
    /**
     * Optional callback when the Pill is clicked.
     */
    readonly onClick: (
        event: React.MouseEvent<HTMLElement, MouseEvent>
    ) => void;

    /**
     * Determines the media props for pill
     */
    readonly pillMedia?: PillMediaProps;

    /**
     * Determines the background and icon color for pill
     */
    readonly severity?: Severity;
}

export interface PillOperationDataProps {
    /**
     * Contain information of the operation dropdown with attach it self to
     */
    readonly progressViewOperationData: TwoUIProgressViewOperationData;

    /**
     * Error message for when the operation fails.
     */
    readonly errorMessage: string;

    /**
     * Error aria-label for the callout shown when the operation fails.
     */
    readonly errorMessageAriaLabel: string;

    /**
     * Text which will show when in the updating state.
     */
    readonly textWhileUpdating?: string;
}

export const Pill: React.FC<PillProps> = ({
    text,
    disabled,
    onClick,
    operationData,
    pillMedia,
    severity,
    ...otherProps
}) => {
    useComponentLogger("Pill", otherProps.id);

    validateProps("Pill", otherProps);

    const theme = useTheme();

    const styles = getStyles(theme, severity);

    const computedPill = (
        <DefaultButton
            disabled={disabled}
            onClick={onClick}
            styles={styles}
            iconProps={getIconProps(theme, pillMedia, severity)}
            {...otherProps}
        >
            <Text block={true} nowrap={true} variant={getTextVariant(severity)}>{text}</Text>
        </DefaultButton>
    );

    const spanRef = useRef<HTMLSpanElement>(null);
    if (operationData) {
        const errorView = (
            <span ref={spanRef} style={{ paddingBottom: "11px" }}>
                {computedPill}
                <ErrorCallout
                    targetOrTriggerButton={spanRef}
                    errorMessageAriaLabel={"Error message aria label"}
                    errorMessage={operationData.errorMessage}
                    calloutPosition={"top-auto"}
                />
            </span>
        );

        return (
            <TwoUIProgressView
                operationData={operationData.progressViewOperationData}
                updatingView={(
                    <PillShimmer charactersInPill={text.length} />
                )}
                finishedView={computedPill}
                failedView={errorView}
            />
        );
    } else {
        return computedPill;
    }
};
